import React, { useCallback, useEffect, useMemo } from 'react';

import { useForm } from 'react-hook-form';
import dayjs from '~/timeInit';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField } from '@mui/material';

import { DateTimeInput, Input, Select } from '~/components/Common/Forms/fields';

import { useUpdatePointsPurchaseOfferCampaign } from '~/hooks/apis/loyalty/usePointsPurchaseMutations';

import {
  POINTS_PURCHASE_CAMPAIGN_STORED_STATUSES,
  type PointsPurchaseOffer,
  type PointsPurchaseOfferCampaign,
  type PointsPurchaseOfferCampaignStoredStatus,
} from '~/types/loyaltyContract';

import { formatCampaignScope, formatCampaignType, toStoredStatus } from './utils';

interface EditCampaignDialogProps {
  open: boolean;
  onClose: () => void;
  campaign: PointsPurchaseOfferCampaign;
  offersMap: Record<string, PointsPurchaseOffer>;
}

interface EditCampaignFormData {
  status: PointsPurchaseOfferCampaignStoredStatus;
  value: string;
  startDate: string;
  endDate: string;
}

function buildDefaultValues(campaign: PointsPurchaseOfferCampaign): EditCampaignFormData {
  return {
    status: toStoredStatus(campaign.status),
    value: campaign.value != null ? String(campaign.value) : '',
    startDate: campaign.startDate ? dayjs(campaign.startDate).format('YYYY-MM-DDTHH:mm') : '',
    endDate: campaign.endDate ? dayjs(campaign.endDate).format('YYYY-MM-DDTHH:mm') : '',
  };
}

export default function EditCampaignDialog({ open, onClose, campaign, offersMap }: EditCampaignDialogProps) {
  const updateMutation = useUpdatePointsPurchaseOfferCampaign();

  const defaultValues = useMemo(() => buildDefaultValues(campaign), [campaign]);

  const { control, handleSubmit, reset, getValues } = useForm<EditCampaignFormData>({
    defaultValues,
    mode: 'onBlur',
  });

  useEffect(() => {
    if (open && campaign) {
      reset(buildDefaultValues(campaign));
    }
  }, [open, campaign, reset]);

  const validateEndDate = (value: string) => {
    if (!value) {
      return true;
    }
    const startDate = getValues('startDate');
    if (startDate && dayjs(value).isBefore(dayjs(startDate))) {
      return 'End date must be after start date';
    }
    return true;
  };

  const onSubmit = useCallback(
    async (data: EditCampaignFormData) => {
      if (!campaign) {
        return;
      }
      try {
        await updateMutation.mutateAsync({
          campaignId: campaign.id,
          params: {
            status: data.status,
            value: Number(data.value),
            startDate: dayjs(data.startDate).toISOString(),
            endDate: data.endDate ? dayjs(data.endDate).toISOString() : null,
          },
        });
        onClose();
      } catch {
        // Error handled in mutation hook
      }
    },
    [campaign, updateMutation, onClose],
  );

  if (!campaign) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Campaign</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Type" value={formatCampaignType(campaign.type)} disabled fullWidth />
          <TextField label="Scope" value={formatCampaignScope(campaign, offersMap).label} disabled fullWidth />

          <Select control={control} name="status" muiLabel="Status" rules={{ required: 'Status is required' }}>
            {POINTS_PURCHASE_CAMPAIGN_STORED_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>

          <Input
            control={control}
            name="value"
            muiLabel="Value"
            type="number"
            rules={{
              required: 'Value is required',
              validate: (v: string) => {
                const n = Number(v);
                if (!Number.isInteger(n)) return 'Must be a whole number';
                if (n <= 0) return 'Must be greater than 0';
                if (n > 100) return 'Must be at most 100';
                return true;
              },
            }}
            fullWidth
            required
          />
          <DateTimeInput
            control={control}
            name="startDate"
            label="Start Date"
            rules={{ required: 'Start date is required' }}
            fullWidth
            required
          />
          <DateTimeInput
            control={control}
            name="endDate"
            label="End Date"
            rules={{ validate: validateEndDate, deps: ['startDate'] }}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={updateMutation.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={updateMutation.isPending}>
          {updateMutation.isPending ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
